import { useState, useCallback, useRef } from 'react'
import { useAuthenticatedFetch, type GenerateRequest, type Generation } from '../lib/api'

interface UseGenerateResult {
  isGenerating: boolean
  error: string | null
  lastGeneration: Generation | null
  generate: (request: GenerateRequest) => Promise<Generation | null>
}

export default function useGenerate(): UseGenerateResult {
  const authenticatedFetch = useAuthenticatedFetch()
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastGeneration, setLastGeneration] = useState<Generation | null>(null)
  const errorTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current)
    }
  }, [])

  const setErrorWithAutoClear = useCallback(
    (message: string) => {
      clearErrorTimer()
      setError(message)
      errorTimerRef.current = setTimeout(() => setError(null), 5000)
    },
    [clearErrorTimer],
  )

  const generate = useCallback(
    async (request: GenerateRequest): Promise<Generation | null> => {
      setIsGenerating(true)
      setError(null)
      clearErrorTimer()

      try {
        const res = await authenticatedFetch('/api/generate', {
          method: 'POST',
          body: JSON.stringify(request),
        })
        const { data } = (await res.json()) as { data: Generation }
        setLastGeneration(data)
        return data
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Generation failed'
        setErrorWithAutoClear(message)
        return null
      } finally {
        setIsGenerating(false)
      }
    },
    [authenticatedFetch, clearErrorTimer, setErrorWithAutoClear],
  )

  return { isGenerating, error, lastGeneration, generate }
}
